import { FieldValues, FormState, UseFormRegister, UseFormSetValue } from 'react-hook-form';

import {
	FieldLayout,
	Fieldset,
	FormField,
	Input,
	Label,
	Radio,
	RadioCheckboxGroup,
} from '@anwb/poncho/components/forms';
import Panel from '@anwb/poncho/components/panel';
import { useTransformVisualValue } from '@anwb/poncho/utilities/form-helpers';
import {
	formatInitials,
	formatSurname,
	formatSurnamePrefix,
} from '@anwb/poncho/utilities/formatters';
import initialsRegexValidation from '@anwb/poncho/validations/validations-initials';
import surnameRegexValidation from '@anwb/poncho/validations/validations-surname';
import surnamePrefixRegexValidation from '@anwb/poncho/validations/validations-surname-prefix';
import { useGaFunnelInfo } from 'hooks/useGaFunnelInfo';
import { PLCustomerData } from 'types';
import { trackEvent } from 'utils/trackEvent';

import { handleFormGaEvent } from '../utils';

import { DateInputField } from './DateInputField';

import { StyledTypography } from './styles/fieldset-caption.styled';

type PersonalInformationProps = {
	register: UseFormRegister<FieldValues>;
	setValue: UseFormSetValue<FieldValues>;
	formState: FormState<PLCustomerData>;
	trigger: (name?: string | string[]) => Promise<boolean>;
};

export const PersonalInformation = ({
	register,
	setValue,
	formState,
	trigger,
}: PersonalInformationProps) => {
	const [, transformedInitials, handleInitialChange] = useTransformVisualValue('', [
		formatInitials,
	]);
	const [, transformedSurname, handleSurnameChange] = useTransformVisualValue('', [formatSurname]);
	const [, transformedSurnamePrefix, handleSurnamePrefixChange] = useTransformVisualValue('', [
		formatSurnamePrefix,
	]);

	const gaFunnelInfo = useGaFunnelInfo();

	const validateInitials = (value: string) => {
		const cleanedValue = value.replace(/[. ]/g, '');

		if (!new RegExp(initialsRegexValidation).test(value)) {
			return 'Gebruik alleen de eerste letters van je voornaam/voornamen.';
		}

		if (cleanedValue.length > 6) {
			return 'Gebruik de eerste letter van je voornaam/voornamen, maximaal 6.';
		}

		return true;
	};

	register('gender', {
		onChange: () => {
			trackEvent({
				event: 'user_interaction',
				type: 'form',
				component: 'form-field-input-radio-button-group',
				field: 'salutation',
				action: 'change',
				status: 'valid',
				...gaFunnelInfo,
			});
		},
	});

	return (
		<Panel hasOuterSpacing={false}>
			<Panel.Content>
				<Fieldset
					legend={
						<StyledTypography variant="component-title" tagName="h2">
							Persoonlijke gegevens
						</StyledTypography>
					}
				>
					<FieldLayout>
						<FormField size="m" label={<Label text="Aanhef" reference="gender" required />}>
							<RadioCheckboxGroup orientation="horizontal">
								<Radio
									reference="male"
									value="MALE"
									{...register('gender', {
										required: 'Je hebt je aanhef nog niet ingevuld.',
									})}
								>
									Meneer
								</Radio>
								<Radio
									reference="female"
									value="FEMALE"
									{...register('gender', {
										required: 'Je hebt je aanhef nog niet ingevuld.',
									})}
								>
									Mevrouw
								</Radio>
							</RadioCheckboxGroup>
							{formState.errors.gender && (
								<FormField.Error aria-live="assertive">
									{formState.errors.gender.message}
								</FormField.Error>
							)}
						</FormField>
					</FieldLayout>
					<FieldLayout>
						<FormField size="m" label={<Label text="Voorletters" reference="initials" required />}>
							<Input
								reference="initials"
								{...register('initials', {
									required: 'Je hebt je voorletters nog niet ingevuld.',
									validate: validateInitials,
								})}
								onChange={(event) => {
									handleInitialChange(event);
									setValue('initials', event.target.value);
								}}
								onBlur={async (event) => {
									if (transformedInitials) {
										setValue('initials', transformedInitials, {
											shouldDirty: true,
										});
										await trigger('initials');
									}
									handleFormGaEvent('initials', formState, event, gaFunnelInfo);
								}}
							/>

							{formState.errors.initials && (
								<FormField.Error aria-live="assertive">
									{formState.errors.initials.message}
								</FormField.Error>
							)}
						</FormField>
						<FormField
							size="m"
							label={<Label text="Tussenvoegsel(s)" reference="lastNamePrefix" />}
						>
							<Input
								reference="lastNamePrefix"
								{...register('lastNamePrefix', {
									pattern: {
										value: new RegExp(surnamePrefixRegexValidation),
										message: 'Het tussenvoegsel(s) bestaat alleen uit letters.',
									},
								})}
								onChange={(event) => {
									handleSurnamePrefixChange(event);
									setValue('lastNamePrefix', event.target.value);
								}}
								onBlur={async (event) => {
									setValue(
										'lastNamePrefix',
										transformedSurnamePrefix ? transformedSurnamePrefix.trim() : '',
										{ shouldDirty: true }
									);
									await trigger('lastNamePrefix');
									handleFormGaEvent('lastNamePrefix', formState, event, gaFunnelInfo);
								}}
							/>

							{formState.errors.lastNamePrefix && (
								<FormField.Error aria-live="assertive">
									{formState.errors.lastNamePrefix.message}
								</FormField.Error>
							)}
						</FormField>
					</FieldLayout>
					<FieldLayout>
						<FormField size="m" label={<Label text="Achternaam" reference="lastName" required />}>
							<Input
								reference="lastName"
								{...register('lastName', {
									required: 'Er is nog geen achternaam ingevuld.',
									pattern: {
										value: new RegExp(surnameRegexValidation),
										message: 'Vul je achternaam in zoals deze op je Europese paspoort staat.',
									},
								})}
								onChange={(event) => {
									handleSurnameChange(event);
									setValue('lastName', event.target.value);
								}}
								onBlur={async (event) => {
									if (transformedSurname) {
										setValue('lastName', transformedSurname, { shouldDirty: true });
										await trigger('lastName');
									}
									handleFormGaEvent('lastName', formState, event, gaFunnelInfo);
								}}
							/>

							{formState.errors.lastName && (
								<FormField.Error aria-live="assertive">
									{formState.errors.lastName.message}
								</FormField.Error>
							)}
						</FormField>
					</FieldLayout>
					<FieldLayout>
						<DateInputField
							register={register}
							setValue={setValue}
							formState={formState}
							trigger={trigger}
						/>
					</FieldLayout>
				</Fieldset>
			</Panel.Content>
		</Panel>
	);
};
